import { lineStrings } from 'constants/strings';
import {
  CARD_TYPE_ALT_TEXT_MAX_LENGTH,
  CARD_TYPE_MAX_CARDS,
  CARD_TYPE_MESSAGE_ACTION_TEXT_MAX_LENGTH,
  CARD_TYPE_URI_MAX_LENGTH,
} from 'constants/cardTypeMessageTemplates';
import {
  ECardTypeActionType,
  ECardTypeTemplateId,
  TCardTypeAction,
  TCardTypeCard,
} from 'types/cardTypeMessage';
import { normalizeCardTypeActionUri } from 'utils/cardTypeMessageUri';
import { countGraphemeClusters } from 'utils/grapheme';

const msgs = lineStrings.management.cardTypeMessage.errors;

export type TCardTypeActionFieldErrors = {
  label?: string;
  actionType?: string;
  uri?: string;
  text?: string;
};

export type TCardTypeCardFieldErrors = {
  header?: string;
  name?: string;
  image?: string;
  tag?: string;
  tags?: Partial<Record<0 | 1 | 2, string>>;
  description?: string;
  price?: string;
  price2?: string;
  address?: string;
  additionalInfo?: string;
};

export type TCardTypeEditorValidationResult = {
  ok: boolean;
  nameError?: string;
  altTextError?: string;
  cardError?: string;
  cardFieldErrors: Record<number, TCardTypeCardFieldErrors>;
  actionErrors: Record<string, TCardTypeActionFieldErrors>;
  firstErrorCardIndex?: number;
  firstErrorActionIndex?: number;
};

function actionErrorKey(cardIndex: number, actionIndex: number): string {
  return `${cardIndex}-${actionIndex}`;
}

function getActionCountForTemplate(templateId: ECardTypeTemplateId): number {
  return templateId === ECardTypeTemplateId.Pictures ? 1 : 2;
}

function requiredTextError(text: string | undefined, message: string): string | undefined {
  if (!text?.trim()) return message;
  return undefined;
}

function validateUriValue(action: TCardTypeAction): Pick<TCardTypeActionFieldErrors, 'uri'> {
  const field: Pick<TCardTypeActionFieldErrors, 'uri'> = {};
  const raw = action.uri ?? '';
  const trimmed = raw.trim();

  if (!trimmed) {
    field.uri = msgs.uriRequired;
  } else {
    const normalized = normalizeCardTypeActionUri(raw);
    if (!normalized) {
      field.uri = msgs.uriInvalid;
    } else if (normalized.length > CARD_TYPE_URI_MAX_LENGTH) {
      field.uri = msgs.uriTooLong;
    }
  }

  return field;
}

function validateAction(action: TCardTypeAction): TCardTypeActionFieldErrors {
  const field: TCardTypeActionFieldErrors = {};
  if (!action.enabled) return field;

  if (!action.label?.trim()) {
    field.label = msgs.labelRequired;
  }

  if (!action.actionType) {
    field.actionType = msgs.actionTypeRequired;
    return field;
  }

  if (action.actionType === ECardTypeActionType.Uri) {
    Object.assign(field, validateUriValue(action));
  } else if (action.actionType === ECardTypeActionType.Text) {
    if (!action.text?.trim()) {
      field.text = msgs.textRequired;
    } else if (
      countGraphemeClusters(action.text) >
      CARD_TYPE_MESSAGE_ACTION_TEXT_MAX_LENGTH
    ) {
      field.text = msgs.textTooLong;
    }
  }

  return field;
}

function validateCardActions(
  card: TCardTypeCard,
  cardIndex: number,
  templateId: ECardTypeTemplateId,
  actionErrors: Record<string, TCardTypeActionFieldErrors>,
): void {
  const actionCount = getActionCountForTemplate(templateId);

  card.actions.slice(0, actionCount).forEach((action, actionIndex) => {
    const field = validateAction(action);
    if (Object.keys(field).length > 0) {
      actionErrors[actionErrorKey(cardIndex, actionIndex)] = field;
    }
  });
}

function validateRequiredCardField(
  card: TCardTypeCard,
  cardIndex: number,
  templateId: ECardTypeTemplateId,
  cardFieldErrors: Record<number, TCardTypeCardFieldErrors>,
): boolean {
  let error: TCardTypeCardFieldErrors = {};

  if (templateId === ECardTypeTemplateId.Pictures && !card.imageUrl?.trim()) {
    error = { image: msgs.imageRequired };
  } else if (templateId === ECardTypeTemplateId.People && !card.name?.trim()) {
    error = { name: msgs.nameFieldRequired };
  } else if (
    (templateId === ECardTypeTemplateId.Products ||
      templateId === ECardTypeTemplateId.Places) &&
    !card.header?.trim()
  ) {
    error = { header: msgs.headerRequired };
  }

  if (Object.keys(error).length === 0) return true;

  cardFieldErrors[cardIndex] = {
    ...cardFieldErrors[cardIndex],
    ...error,
  };
  return false;
}

function validateOptionalCardFields(
  card: TCardTypeCard,
  cardIndex: number,
  templateId: ECardTypeTemplateId,
  cardFieldErrors: Record<number, TCardTypeCardFieldErrors>,
): void {
  const errors: TCardTypeCardFieldErrors = {};

  if (
    templateId !== ECardTypeTemplateId.People &&
    card.tag?.enabled
  ) {
    const tagError = requiredTextError(card.tag.text, msgs.tagTextRequired);
    if (tagError) errors.tag = tagError;
  }

  if (templateId === ECardTypeTemplateId.People) {
    const tagErrors: Partial<Record<0 | 1 | 2, string>> = {};
    card.tags?.forEach((tag, index) => {
      if (!tag.enabled) return;
      const tagError = requiredTextError(tag.text, msgs.tagTextRequired);
      if (tagError) tagErrors[index as 0 | 1 | 2] = tagError;
    });
    if (Object.keys(tagErrors).length > 0) {
      errors.tags = tagErrors;
    }
  }

  if (
    (templateId === ECardTypeTemplateId.Products ||
      templateId === ECardTypeTemplateId.People) &&
    card.description?.enabled
  ) {
    const descriptionError = requiredTextError(
      card.description.text,
      msgs.descriptionRequired,
    );
    if (descriptionError) errors.description = descriptionError;
  }

  if (templateId === ECardTypeTemplateId.Products && card.price?.enabled) {
    const priceError = requiredTextError(card.price.value, msgs.priceRequired);
    if (priceError) errors.price = priceError;
  }

  if (templateId === ECardTypeTemplateId.Products && card.price2?.enabled) {
    const price2Error = requiredTextError(
      card.price2.value,
      msgs.price2Required,
    );
    if (price2Error) errors.price2 = price2Error;
  }

  if (templateId === ECardTypeTemplateId.Places && card.address?.enabled) {
    const addressError = requiredTextError(
      card.address.text,
      msgs.addressRequired,
    );
    if (addressError) errors.address = addressError;
  }

  if (
    templateId === ECardTypeTemplateId.Places &&
    card.additionalInfo?.enabled
  ) {
    const additionalInfoError = requiredTextError(
      card.additionalInfo.text,
      msgs.additionalInfoRequired,
    );
    if (additionalInfoError) errors.additionalInfo = additionalInfoError;
  }

  if (Object.keys(errors).length > 0) {
    cardFieldErrors[cardIndex] = {
      ...cardFieldErrors[cardIndex],
      ...errors,
    };
  }
}

function validateVisibleSecondaryImages(
  card: TCardTypeCard,
  cardIndex: number,
  templateId: ECardTypeTemplateId,
  cardFieldErrors: Record<number, TCardTypeCardFieldErrors>,
): void {
  if (
    templateId !== ECardTypeTemplateId.Products &&
    templateId !== ECardTypeTemplateId.Places
  ) {
    return;
  }

  const images = {
    a: card.images?.a?.trim() || card.imageUrl?.trim() || '',
    b: card.images?.b?.trim() || '',
    c: card.images?.c?.trim() || '',
  };
  const imageCount = card.imageCount ?? 1;
  const hasVisibleSecondary =
    (imageCount >= 2 && Boolean(images.b)) ||
    (imageCount >= 3 && Boolean(images.c));

  if (images.a || !hasVisibleSecondary) return;

  cardFieldErrors[cardIndex] = {
    ...cardFieldErrors[cardIndex],
    image: msgs.imageRequired,
  };
}

function findFirstErrorCardIndex(
  cardFieldErrors: Record<number, TCardTypeCardFieldErrors>,
  actionErrors: Record<string, TCardTypeActionFieldErrors>,
): number | undefined {
  const cardIndices = new Set<number>();

  Object.keys(cardFieldErrors).forEach((key) => {
    cardIndices.add(Number.parseInt(key, 10));
  });
  Object.keys(actionErrors).forEach((key) => {
    cardIndices.add(Number.parseInt(key.split('-')[0]!, 10));
  });

  if (cardIndices.size === 0) return undefined;
  return Math.min(...cardIndices);
}

export function validateCardTypeMessageEditor(params: {
  name: string;
  altText: string;
  templateId: ECardTypeTemplateId;
  cards: TCardTypeCard[];
}): TCardTypeEditorValidationResult {
  let nameError: string | undefined;
  let altTextError: string | undefined;
  let cardError: string | undefined;
  const cardFieldErrors: Record<number, TCardTypeCardFieldErrors> = {};
  const actionErrors: Record<string, TCardTypeActionFieldErrors> = {};

  if (!params.name.trim()) {
    nameError = msgs.nameRequired;
  }
  if (!params.altText.trim()) {
    altTextError = msgs.altTextRequired;
  } else if (params.altText.length > CARD_TYPE_ALT_TEXT_MAX_LENGTH) {
    altTextError = msgs.altTextTooLong;
  }

  let allCardsHaveRequiredField = params.cards.length > 0;
  params.cards.forEach((card, cardIndex) => {
    if (
      !validateRequiredCardField(
        card,
        cardIndex,
        params.templateId,
        cardFieldErrors,
      )
    ) {
      allCardsHaveRequiredField = false;
    }
    validateVisibleSecondaryImages(
      card,
      cardIndex,
      params.templateId,
      cardFieldErrors,
    );
    validateOptionalCardFields(
      card,
      cardIndex,
      params.templateId,
      cardFieldErrors,
    );
    validateCardActions(card, cardIndex, params.templateId, actionErrors);
  });

  if (params.cards.length > CARD_TYPE_MAX_CARDS) {
    cardError = msgs.maxCardsReached;
  } else if (!allCardsHaveRequiredField) {
    if (params.templateId === ECardTypeTemplateId.People) {
      cardError = msgs.nameFieldRequired;
    } else if (params.templateId === ECardTypeTemplateId.Pictures) {
      cardError = msgs.imageRequired;
    } else {
      cardError = msgs.headerRequired;
    }
  }

  const ok =
    !nameError &&
    !altTextError &&
    !cardError &&
    Object.keys(cardFieldErrors).length === 0 &&
    Object.keys(actionErrors).length === 0;

  const firstErrorCardIndex = findFirstErrorCardIndex(
    cardFieldErrors,
    actionErrors,
  );

  let firstErrorActionIndex: number | undefined;
  if (firstErrorCardIndex !== undefined) {
    const firstKey = Object.keys(actionErrors)
      .filter((key) => key.startsWith(`${firstErrorCardIndex}-`))
      .sort()[0];
    if (firstKey) {
      firstErrorActionIndex = Number.parseInt(firstKey.split('-')[1]!, 10);
    }
  }

  return {
    ok,
    nameError,
    altTextError,
    cardError,
    cardFieldErrors,
    actionErrors,
    firstErrorCardIndex,
    firstErrorActionIndex,
  };
}
